import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
} from '@mui/material';
import {
  Security,
  Notifications,
  Language,
  Palette,
  Delete,
  Key,
  Shield,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useBrandColors } from '../hooks/useBrandColors';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';

export const AccountSettings: React.FC = () => {
  const brandColors = useBrandColors();
  const { state: authState } = useAuth();
  const [settings, setSettings] = useState({
    emailNotifications: true,
    pushNotifications: false,
    weeklyReports: true,
    darkMode: false,
    language: 'en',
    timezone: 'UTC',
    twoFactorAuth: false,
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [saveMessage, setSaveMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // Load account settings on component mount
  useEffect(() => {
    const loadAccountSettings = async () => {
      try {
        const accountSettings = await apiService.getAccountSettings();
        setSettings(accountSettings);
      } catch (error) {
        console.error('Error loading account settings:', error);
        setSaveMessage('Failed to load account settings');
        setTimeout(() => setSaveMessage(''), 3000);
      }
    };

    loadAccountSettings();
  }, []);

  const handleSettingChange = (setting: string, value: boolean | string) => {
    setSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      await apiService.updateAccountSettings(settings);
      setSaveMessage('Account settings updated successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Error saving account settings:', error);
      setSaveMessage('Failed to save account settings');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setSaveMessage('New passwords do not match!');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setSaveMessage('New password must be at least 6 characters long!');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }

    setLoading(true);
    try {
      await apiService.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      setSaveMessage('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Error changing password:', error);
      setSaveMessage('Failed to change password. Please check your current password.');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field: string) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field as keyof typeof prev]
    }));
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Security sx={{ fontSize: 32, color: brandColors.primary, mr: 2 }} />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Account Settings
        </Typography>
      </Box>

      {saveMessage && (
        <Alert 
          severity={saveMessage.includes('not match') || saveMessage.includes('Failed') || saveMessage.includes('characters long') ? 'error' : 'success'} 
          sx={{ mb: 3 }}
        >
          {saveMessage}
        </Alert>
      )}

      {/* Security Settings */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Shield sx={{ fontSize: 24, color: brandColors.primary, mr: 2 }} />
            <Typography variant="h6">Security</Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Change Password
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Current Password"
                type={showPasswords.current ? 'text' : 'password'}
                value={passwordData.currentPassword}
                onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <Button
                      onClick={() => togglePasswordVisibility('current')}
                      sx={{ minWidth: 'auto', p: 1 }}
                    >
                      {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                    </Button>
                  ),
                }}
              />
              <TextField
                label="New Password"
                type={showPasswords.new ? 'text' : 'password'}
                value={passwordData.newPassword}
                onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <Button
                      onClick={() => togglePasswordVisibility('new')}
                      sx={{ minWidth: 'auto', p: 1 }}
                    >
                      {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                    </Button>
                  ),
                }}
              />
              <TextField
                label="Confirm New Password"
                type={showPasswords.confirm ? 'text' : 'password'}
                value={passwordData.confirmPassword}
                onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <Button
                      onClick={() => togglePasswordVisibility('confirm')}
                      sx={{ minWidth: 'auto', p: 1 }}
                    >
                      {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                    </Button>
                  ),
                }}
              />
              <Button
                variant="contained"
                startIcon={<Key />}
                onClick={handleChangePassword}
                disabled={loading}
                sx={{ 
                  alignSelf: 'flex-start',
                  backgroundColor: brandColors.primary, 
                  '&:hover': { backgroundColor: brandColors.secondary }
                }}
              >
                {loading ? 'Changing...' : 'Change Password'}
              </Button>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <FormControlLabel
            control={
              <Switch
                checked={settings.twoFactorAuth}
                onChange={(e) => handleSettingChange('twoFactorAuth', e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1">Two-Factor Authentication</Typography>
                <Typography variant="body2" color="text.secondary">
                  Add an extra layer of security to your account
                </Typography>
              </Box>
            }
          />
        </CardContent>
      </Card>

      {/* Notification Settings */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Notifications sx={{ fontSize: 24, color: brandColors.primary, mr: 2 }} />
            <Typography variant="h6">Notifications</Typography>
          </Box>

          <List>
            <ListItem>
              <ListItemText
                primary="Email Notifications"
                secondary="Receive notifications via email"
              />
              <ListItemSecondaryAction>
                <Switch
                  checked={settings.emailNotifications}
                  onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                  color="primary"
                />
              </ListItemSecondaryAction>
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Push Notifications"
                secondary="Receive push notifications in your browser"
              />
              <ListItemSecondaryAction>
                <Switch
                  checked={settings.pushNotifications}
                  onChange={(e) => handleSettingChange('pushNotifications', e.target.checked)}
                  color="primary"
                />
              </ListItemSecondaryAction>
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Weekly Reports"
                secondary="Receive weekly productivity reports"
              />
              <ListItemSecondaryAction>
                <Switch
                  checked={settings.weeklyReports}
                  onChange={(e) => handleSettingChange('weeklyReports', e.target.checked)}
                  color="primary"
                />
              </ListItemSecondaryAction>
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Preferences */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Palette sx={{ fontSize: 24, color: brandColors.primary, mr: 2 }} />
            <Typography variant="h6">Preferences</Typography>
          </Box>

          <List>
            <ListItem>
              <ListItemText
                primary="Dark Mode"
                secondary="Use dark theme for the interface"
              />
              <ListItemSecondaryAction>
                <Switch
                  checked={settings.darkMode}
                  onChange={(e) => handleSettingChange('darkMode', e.target.checked)}
                  color="primary"
                />
              </ListItemSecondaryAction>
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Language"
                secondary="Choose your preferred language"
              />
              <ListItemSecondaryAction>
                <Chip label="English" size="small" />
              </ListItemSecondaryAction>
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Timezone"
                secondary="Set your local timezone"
              />
              <ListItemSecondaryAction>
                <Chip label="UTC" size="small" />
              </ListItemSecondaryAction>
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="contained"
          onClick={handleSaveSettings}
          disabled={loading}
          sx={{ 
            backgroundColor: brandColors.primary, 
            '&:hover': { backgroundColor: brandColors.secondary }
          }}
        >
          {loading ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
    </Box>
  );
};
